import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft, KeyRound } from "lucide-react";
import { useAuth } from "@/lib/auth";

const ALLOWED_STAFF_ROLES = ["super_admin", "admin", "staff", "operations", "finance", "content",
    "otel_owner", "otel_manager", "otel_reception", "otel_accounting"];
const HOTEL_ROLES = ["otel_owner", "otel_manager", "otel_reception", "otel_accounting"];

export default function StaffLoginPage() {
    const { login, verify2FA } = useAuth() || {};
    const navigate = useNavigate();
    const location = useLocation();

    // Step 1: email/password
    const [email, setEmail] = useState(location.state?.email || "");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(location.state?.notice || "");

    // Step 2: 2FA
    const [step, setStep] = useState(1); // 1 = creds, 2 = 2fa
    const [pendingToken, setPendingToken] = useState("");
    const [code, setCode] = useState("");
    const [useBackup, setUseBackup] = useState(false);

    const goToAdmin = (user) => {
        if (!ALLOWED_STAFF_ROLES.includes(user.role)) {
            setError("Bu giriş sadece personel/yönetici hesapları içindir.");
            return;
        }
        // Hotel roles go to the hotel panel; platform staff go to /admin.
        const defaultPath = HOTEL_ROLES.includes(user.role) ? "/h/dashboard" : "/admin";
        const next = location.state?.from || defaultPath;
        navigate(next);
    };

    const onSubmitCreds = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const res = await login(email, password);
            if (res.requires_2fa) {
                setPendingToken(res.pending_token);
                setStep(2);
                setLoading(false);
                return;
            }
            goToAdmin(res);
        } catch (err) {
            setError(err.response?.data?.detail || "Giriş başarısız");
        } finally {
            setLoading(false);
        }
    };

    const onSubmit2FA = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const user = await verify2FA(pendingToken, code);
            goToAdmin(user);
        } catch (err) {
            setError(err.response?.data?.detail || "Doğrulama başarısız");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="staff-login-page" className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4">
            <div className="w-full max-w-md">
                {step === 1 ? (
                    <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-6 transition-colors" data-testid="staff-back-to-site">
                        <ArrowLeft className="h-4 w-4" />
                        Siteye dön
                    </Link>
                ) : (
                    <button type="button" onClick={() => { setStep(1); setCode(""); setError(""); setPendingToken(""); }} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-6 transition-colors" data-testid="staff-2fa-back">
                        <ArrowLeft className="h-4 w-4" />
                        Geri
                    </button>
                )}

                <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700 shadow-2xl p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-sky-500/30">
                            {step === 1 ? <ShieldCheck className="h-6 w-6 text-white" /> : <KeyRound className="h-6 w-6 text-white" />}
                        </div>
                        <div>
                            <h1 className="font-display text-2xl font-bold text-white">{step === 1 ? "Personel Girişi" : "İki Adımlı Doğrulama"}</h1>
                            <p className="text-xs text-slate-400">{step === 1 ? "Yönetim paneli erişimi" : useBackup ? "Yedek kodla giriş" : "Authenticator app'inizden 6 haneli kodu girin"}</p>
                        </div>
                    </div>

                    {step === 1 ? (
                        <form onSubmit={onSubmitCreds} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-1.5">Personel E-posta</label>
                                <input
                                    data-testid="staff-login-email"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    autoComplete="username"
                                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900/60 border border-slate-700 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 text-sm font-medium text-white outline-none transition-colors"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-1.5">Şifre</label>
                                <input
                                    data-testid="staff-login-password"
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900/60 border border-slate-700 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 text-sm font-medium text-white outline-none transition-colors"
                                />
                            </div>
                            {error && (
                                <div data-testid="staff-login-error" className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
                                    {error}
                                </div>
                            )}
                            <button
                                data-testid="staff-login-submit"
                                type="submit"
                                disabled={loading}
                                className="w-full py-3 rounded-xl bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-600 hover:to-indigo-700 text-white font-semibold shadow-lg shadow-sky-500/20 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                            >
                                {loading ? "Giriş yapılıyor..." : "Yönetim Paneline Giriş"}
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={onSubmit2FA} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-1.5">
                                    {useBackup ? "Yedek Kod" : "6 Haneli Kod"}
                                </label>
                                <input
                                    data-testid="staff-2fa-code"
                                    type="text"
                                    required
                                    autoFocus
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    placeholder={useBackup ? "XXXX-XXXX" : "123456"}
                                    inputMode={useBackup ? "text" : "numeric"}
                                    maxLength={useBackup ? 9 : 6}
                                    className="w-full px-3.5 py-3 rounded-xl bg-slate-900/60 border border-slate-700 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 text-2xl font-mono font-bold tracking-[0.4em] text-white text-center outline-none transition-colors"
                                />
                            </div>
                            {error && (
                                <div data-testid="staff-2fa-error" className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
                                    {error}
                                </div>
                            )}
                            <button
                                data-testid="staff-2fa-submit"
                                type="submit"
                                disabled={loading || !code}
                                className="w-full py-3 rounded-xl bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-600 hover:to-indigo-700 text-white font-semibold shadow-lg shadow-sky-500/20 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                            >
                                {loading ? "Doğrulanıyor..." : "Doğrula"}
                            </button>
                            <button
                                type="button"
                                data-testid="staff-2fa-toggle-backup"
                                onClick={() => { setUseBackup((v) => !v); setCode(""); setError(""); }}
                                className="w-full text-xs text-slate-400 hover:text-sky-400 transition-colors"
                            >
                                {useBackup ? "← Authenticator koduyla giriş yap" : "Authenticator'a erişemiyorum, yedek kod kullan →"}
                            </button>
                        </form>
                    )}

                    <div className="mt-6 pt-6 border-t border-slate-700/50 text-center space-y-1.5">
                        <p className="text-xs text-slate-500">
                            Otel yetkilisi misiniz? <Link to="/h/login" className="text-emerald-400 hover:text-emerald-300 font-medium" data-testid="staff-hotel-link">Otel girişine geçin</Link>
                        </p>
                        <p className="text-xs text-slate-500">
                            Müşteri misiniz? <Link to="/login" className="text-sky-400 hover:text-sky-300 font-medium" data-testid="staff-customer-link">Müşteri girişine geçin</Link>
                        </p>
                    </div>
                </div>

                <p className="text-center text-xs text-slate-600 mt-6">
                    Bu alan sadece yetkili personel içindir. Yetkisiz giriş denemeleri kaydedilir.
                </p>
            </div>
        </div>
    );
}
